import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash.debounce";

import { storage } from "util/storage";

interface StoredDraftPayload<T> {
  savedAtIso: string;
  value: T;
  version: number;
}

interface UseAutoSaveDraftOptions<T> {
  draftKey: string | null;
  value: T;
  enabled?: boolean;
  isDirty: boolean;
  ttlMs?: number;
  version?: number;
  debounceMs?: number;
  restoreMode?: "silent" | "none";
  restoreMessage?: string;
  leaveMessage?: string;
  shouldSave?: (value: T) => boolean;
  onRestore: (value: T) => void;
}

interface AutoSaveDraftResult {
  clearDraft: () => void;
  saveDraftNow: () => void;
  restoredAtIso: string | null;
}

const DEFAULT_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_DEBOUNCE_MS = 600;

/**
 * Persist form state to localStorage and optionally restore it on mount.
 * In-app route changes rely on autosave + restore instead of router-
 * specific blockers so this hook stays compatible with both classic and data
 * router setups.
 */
export function useAutoSaveDraft<T>({
  draftKey,
  value,
  enabled = true,
  isDirty,
  ttlMs = DEFAULT_DRAFT_TTL_MS,
  version = 1,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  restoreMode = "silent",
  shouldSave,
  onRestore,
}: UseAutoSaveDraftOptions<T>): AutoSaveDraftResult {
  const restoreHandledRef = useRef(false);
  const [restoredAtIso, setRestoredAtIso] = useState<string | null>(null);

  const canPersistDraft = useMemo(() => {
    if (!draftKey || !enabled || !isDirty) {
      return false;
    }
    if (typeof shouldSave === "function") {
      return shouldSave(value);
    }
    return true;
  }, [draftKey, enabled, isDirty, shouldSave, value]);

  const clearDraft = useCallback(() => {
    if (!draftKey) {
      return;
    }
    storage.remove(draftKey);
  }, [draftKey]);

  const saveDraftNow = useCallback(() => {
    if (!draftKey || !enabled || !canPersistDraft) {
      return;
    }
    storage.setJSONWithTTL(
      draftKey,
      {
        savedAtIso: new Date().toISOString(),
        value,
        version,
      } satisfies StoredDraftPayload<T>,
      ttlMs
    );
  }, [draftKey, enabled, canPersistDraft, value, version, ttlMs]);

  const debouncedSaveDraft = useMemo(
    () =>
      debounce(() => {
        saveDraftNow();
      }, debounceMs),
    [debounceMs, saveDraftNow]
  );

  useEffect(() => {
    if (!draftKey || !enabled || restoreHandledRef.current) {
      return;
    }
    restoreHandledRef.current = true;

    const storedDraft = storage.getJSONWithTTL<StoredDraftPayload<T> | null>(draftKey, null);
    if (!storedDraft || storedDraft.version !== version) {
      return;
    }

    if (restoreMode === "none") {
      return;
    }

    if (restoreMode === "silent") {
      onRestore(storedDraft.value);
      setRestoredAtIso(storedDraft.savedAtIso);
      return;
    }
  }, [draftKey, enabled, onRestore, restoreMode, version]);

  useEffect(() => {
    if (!draftKey || !enabled) {
      return;
    }

    if (!canPersistDraft) {
      debouncedSaveDraft.cancel();
      return;
    }

    debouncedSaveDraft();
    return () => {
      debouncedSaveDraft.cancel();
    };
  }, [draftKey, enabled, canPersistDraft, debouncedSaveDraft, value]);

  useEffect(() => {
    if (!draftKey || !enabled) {
      return;
    }

    return () => {
      if (canPersistDraft) {
        saveDraftNow();
      }
    };
  }, [draftKey, enabled, canPersistDraft, saveDraftNow]);

  return {
    clearDraft,
    saveDraftNow,
    restoredAtIso,
  };
}
